import { useCallback } from 'react'

import {
  BenefitsEmployeeSelectorLegalEntitiesDocument,
  BenefitsEmployeeSelectorLegalEntityFragment,
  BenefitsNameSelectorDocument,
} from 'generated/resources/graphql'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useLazyQuery, useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'

import { useActiveMarkets } from 'modules/benefits/hooks/useActiveMarkets'
import { type EmployeeDetailsMap } from 'modules/benefits/pages/EmployeesPage/ActivationResultSidePanel'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'

export function useBenefitsEmployeeSelector(companyId: number, employeeIdsWithBenefits: number[]) {
  const { activeMarkets, loading: loadingActiveMarkets } = useActiveMarkets()

  const { data: legalEntitiesData, loading: loadingLegalEntities } = useQuery(
    BenefitsEmployeeSelectorLegalEntitiesDocument,
    {
      variables: { companyId, markets: activeMarkets },
      skip: loadingActiveMarkets,
      fetchPolicy: 'cache-first',
    }
  )

  const legalEntities = useUnsafeMemo(() => {
    return legalEntitiesData?.apiCore?.company?.legalEntities?.filter(isBenefitsEligible)
  }, [legalEntitiesData])

  const legalEntityIds = useUnsafeMemo(
    () => legalEntities?.map((le) => le.id) ?? [],
    [legalEntities]
  )

  const { data: employeeNameData, loading: loadingNames } = useQuery(BenefitsNameSelectorDocument, {
    variables: {
      companyId,
      active: true,
      legalEntityIds,
    },
    skip: loadingActiveMarkets || loadingLegalEntities || !legalEntityIds.length,
  })

  const employeeNameMap = useUnsafeMemo(() => {
    const map = new Map<number, { firstName: string; lastName: string; avatarUrl?: string }>()
    const names = employeeNameData?.employees.employeeNames
    if (!names) return map

    const qualifiedGroupIds = new Set(
      legalEntities?.flatMap((le) => le.employeeGroups.map((g) => g.id)) ?? []
    )

    names.forEach((emp) => {
      if (
        !employeeIdsWithBenefits.includes(emp.id) &&
        emp.employeeGroupId &&
        qualifiedGroupIds.has(emp.employeeGroupId)
      ) {
        map.set(emp.id, {
          firstName: emp.firstName,
          lastName: emp.lastName,
          avatarUrl: emp.avatar ? buildUrl(emp.avatar.url) : undefined,
        })
      }
    })

    return map
  }, [employeeNameData, legalEntities, employeeIdsWithBenefits])

  const [fetchFilteredEmployees] = useLazyQuery(BenefitsNameSelectorDocument)

  const qualifiedGroupIds = useUnsafeMemo(
    () => new Set(legalEntities?.flatMap((le) => le.employeeGroups.map((g) => g.id)) ?? []),
    [legalEntities]
  )

  const buildDetailsMapFromEmployees = useCallback(
    (
      employees: Array<{
        id: number
        firstName: string
        lastName: string
        employeeGroupId?: number | null
        avatar?: { url: string } | null
      }>,
      excludedIds: number[] = []
    ): EmployeeDetailsMap => {
      const map: EmployeeDetailsMap = {}
      const excludedSet = new Set(excludedIds)

      employees.forEach((emp) => {
        if (
          !excludedSet.has(emp.id) &&
          !employeeIdsWithBenefits.includes(emp.id) &&
          emp.employeeGroupId &&
          qualifiedGroupIds.has(emp.employeeGroupId)
        ) {
          map[emp.id] = {
            firstName: emp.firstName,
            lastName: emp.lastName,
            avatarUrl: emp.avatar ? buildUrl(emp.avatar.url) : undefined,
          }
        }
      })

      return map
    },
    [employeeIdsWithBenefits, qualifiedGroupIds]
  )

  const buildDetailsMap = useCallback(
    (selection: EmployeeSelectorV2MultiSelectState): EmployeeDetailsMap => {
      const map: EmployeeDetailsMap = {}

      if (selection.type === 'manual') {
        selection.ids.forEach((id) => {
          const emp = employeeNameMap.get(id)
          if (emp) {
            map[id] = emp
          }
        })
      } else {
        const excludedSet = new Set(selection.excludedIds ?? [])
        employeeNameMap.forEach((emp, id) => {
          if (!excludedSet.has(id)) {
            map[id] = emp
          }
        })
      }

      return map
    },
    [employeeNameMap]
  )

  const resolveSelectionAndBuildDetailsMap = useCallback(
    async (
      selection: EmployeeSelectorV2MultiSelectState
    ): Promise<{
      resolvedSelection: EmployeeSelectorV2MultiSelectState
      employeeDetailsMap: EmployeeDetailsMap
    }> => {
      // If manual selection, use the sync path
      if (selection.type === 'manual') {
        return {
          resolvedSelection: selection,
          employeeDetailsMap: buildDetailsMap(selection),
        }
      }

      // If 'all' with filters, resolve by querying with those filters
      const filters = selection.filters
      const hasFilters = filters && typeof filters === 'object' && Object.keys(filters).length > 0

      if (hasFilters) {
        const locationIds = extractNumberArray(filters.workplace)
        const teamIds = extractNumberArray(filters.team)

        const { data } = await fetchFilteredEmployees({
          variables: {
            companyId,
            active: true,
            legalEntityIds,
            locationIds,
            teamIds,
          },
        })

        if (data?.employees?.employeeNames) {
          const employeeDetailsMap = buildDetailsMapFromEmployees(
            data.employees.employeeNames,
            selection.excludedIds ?? []
          )

          const resolvedIds = Object.keys(employeeDetailsMap).map(Number)

          return {
            resolvedSelection: { type: 'manual', ids: resolvedIds },
            employeeDetailsMap,
          }
        }
      }

      // Fallback: no filters or query failed, use the sync path
      return {
        resolvedSelection: selection,
        employeeDetailsMap: buildDetailsMap(selection),
      }
    },
    [
      buildDetailsMap,
      buildDetailsMapFromEmployees,
      companyId,
      fetchFilteredEmployees,
      legalEntityIds,
    ]
  )

  const loading = loadingActiveMarkets || loadingLegalEntities || loadingNames

  return {
    loading,
    legalEntityIds,
    buildDetailsMap,
    resolveSelectionAndBuildDetailsMap,
  }
}

function isBenefitsEligible(legalEntity: BenefitsEmployeeSelectorLegalEntityFragment): boolean {
  if (legalEntity.benefitsOnboardings.totalCount) return true

  return legalEntity.expensesBankingAccounts?.some((account) =>
    account.bankAccounts?.some((bankAccount) =>
      bankAccount.cardholders.some(
        (cardholder) => (cardholder.cardsConnection?.totalCount ?? 0) > 0
      )
    )
  )
}

function extractNumberArray(value: unknown): number[] | undefined {
  if (!Array.isArray(value) || value.length === 0) return undefined
  if (value.every((item): item is number => typeof item === 'number')) {
    return value
  }
  return undefined
}
